import { FormEvent, useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";

import { apiPost } from "../lib/api";
import { Button, Card, LinkButton, StatusPill } from "./ui";

type AuthMode = "sign-in" | "register";
type AuthState = "idle" | "submitting" | "success" | "error";

type AuthResponse = {
  user: {
    email: string;
    emailVerified: boolean;
  };
  accessToken: string;
  refreshToken: string;
};

export function AuthPage() {
  const query = new URLSearchParams(window.location.search);
  const requestedPlan = query.get("plan");
  const [mode, setMode] = useState<AuthMode>(query.get("mode") === "register" ? "register" : "sign-in");
  const [state, setState] = useState<AuthState>("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    setMessage("");

    const form = new FormData(event.currentTarget);
    const payload = {
      email: String(form.get("email") || ""),
      password: String(form.get("password") || ""),
      referralCode: String(form.get("referralCode") || "") || undefined,
    };

    const confirmPassword = String(form.get("confirmPassword") || "");
    if (mode === "register" && payload.password !== confirmPassword) {
      setState("error");
      setMessage("Passwords do not match.");
      return;
    }

    try {
      const response = await apiPost<AuthResponse>(mode === "register" ? "/auth/register" : "/auth/login", payload);
      window.localStorage.setItem(
        "ibox-web-session",
        JSON.stringify({
          email: response.user.email,
          emailVerified: response.user.emailVerified,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        }),
      );
      setState("success");
      setMessage(
        mode === "register"
          ? "Account created. Check your inbox for the iBox verification link."
          : response.user.emailVerified
            ? "You are signed in. You can return to iBox Desktop."
            : "You are signed in, but email verification is still required before billing.",
      );
      window.location.href = requestedPlan ? `/account?plan=${encodeURIComponent(requestedPlan)}` : "/account";
    } catch (authError) {
      setState("error");
      setMessage(authError instanceof Error ? authError.message : "iBox could not complete sign in.");
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-10 px-6 py-24 lg:grid-cols-[0.82fr_1fr]">
        <div>
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">iBox Account</span>
          </div>
          <h1 className="mb-7 font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">
            Sign in to iBox.
          </h1>
          <p className="max-w-xl text-lg leading-relaxed text-muted md:text-xl">
            Create your iBox account, verify your email, and connect billing for Pro features.
          </p>
        </div>

        <Card interactive={false} className="p-0">
          <div className="flex items-start justify-between gap-4 border-b border-border p-6">
            <div>
              <StatusPill status={state === "success" ? "success" : "info"} label={mode === "register" ? "CREATE ACCOUNT" : "SIGN IN"} />
              <h2 className="mt-5 font-display text-3xl font-bold uppercase leading-none text-text">
                {mode === "register" ? "Create Account" : "Welcome Back"}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                {mode === "register" ? "Start with email and password. Billing can come later." : "Use the same account you use in iBox Desktop."}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-bg text-primary">
              <ShieldCheck size={22} strokeWidth={1.6} />
            </div>
          </div>

          <form className="space-y-5 p-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-md border px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors ${
                  mode === "sign-in" ? "border-primary bg-primary/15 text-text" : "border-border text-muted hover:text-text"
                }`}
                onClick={() => {
                  setMode("sign-in");
                  setState("idle");
                  setMessage("");
                }}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`rounded-md border px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors ${
                  mode === "register" ? "border-primary bg-primary/15 text-text" : "border-border text-muted hover:text-text"
                }`}
                onClick={() => {
                  setMode("register");
                  setState("idle");
                  setMessage("");
                }}
              >
                Register
              </button>
            </div>

            <label className="block space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Email</span>
              <input
                required
                type="email"
                name="email"
                autoComplete="email"
                className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                placeholder="you@example.com"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Password</span>
              <input
                required
                type="password"
                name="password"
                minLength={mode === "register" ? 10 : 1}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                placeholder={mode === "register" ? "At least 10 characters" : "Password"}
              />
            </label>

            {mode === "register" ? (
              <>
                <label className="block space-y-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Confirm Password</span>
                  <input
                    required
                    type="password"
                    name="confirmPassword"
                    autoComplete="new-password"
                    className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                    placeholder="Repeat password"
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Referral Code</span>
                  <input
                    name="referralCode"
                    className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm uppercase text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                    placeholder="Optional"
                  />
                </label>
              </>
            ) : null}

            {state === "success" ? (
              <p className="text-sm font-semibold text-success">{message}</p>
            ) : state === "error" ? (
              <p className="text-sm font-semibold text-error">{message}</p>
            ) : null}

            <Button className="w-full" type="submit" disabled={state === "submitting"}>
              {state === "submitting" ? "Working" : mode === "register" ? "Create Account" : "Sign In"} <ArrowRight size={14} />
            </Button>

            <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row">
              {mode === "sign-in" ? (
                <LinkButton href="/reset-password" variant="ghost" className="flex-1">
                  Forgot Password
                </LinkButton>
              ) : (
                <LinkButton href="/sign-in" variant="ghost" className="flex-1">
                  Already Have an Account
                </LinkButton>
              )}
              <LinkButton href="/download" variant="ghost" className="flex-1">
                Get iBox
              </LinkButton>
            </div>
          </form>
        </Card>
      </section>
    </main>
  );
}
